import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, Button, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import isEmail from 'validator/lib/isEmail';
import { SubmitButton } from './SubmitButton';
import { LoginResult, loginWithCredentials } from './api';
import { MAX_EMAIL, MAX_PASSWORD } from './fieldLimits';
import { autofillSx } from './fieldStyles';
import { useAutofillLabel } from './useAutofillLabel';
import { useFormSubmit } from './useFormSubmit';

interface Props {
  onMfaRequired: (challenge: Extract<LoginResult, { status: 'MFA_REQUIRED' }> & { email: string }) => void;
  onNewPasswordRequired: (state: { session: string; email: string }) => void;
  /** The account must enrol an authenticator before it can sign in. */
  onMfaSetupRequired: (state: { session: string; email: string }) => void;
  /** More than one method is enrolled and none is preferred. */
  onSelectMfaType: (state: Extract<LoginResult, { status: 'SELECT_MFA_TYPE' }> & { email: string }) => void;
  onForgotPassword: () => void;
  /** Language deliberately picked on this page, if any. */
  chosenLang?: string;
}

export const LocalLoginForm = ({
  onMfaRequired,
  onNewPasswordRequired,
  onMfaSetupRequired,
  onSelectMfaType,
  onForgotPassword,
  chosenLang,
}: Props) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { loading, error, setError, runSubmit } = useFormSubmit();
  // Chrome autofills these two on load without telling the form; see useAutofillLabel (CNA-2074).
  const emailAutofill = useAutofillLabel();
  const passwordAutofill = useAutofillLabel();

  /**
   * The form is `noValidate`, so the browser does not check `type="email"` for us. Without this
   * the request goes out and comes back 400 with a raw server message. `validator`'s `isEmail` is
   * the same implementation class-validator's `@IsEmail()` uses on the API side, so the two agree.
   * Only once something has been typed — an empty field is "incomplete", not "wrong".
   */
  const isEmailInvalid = email.length > 0 && !isEmail(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEmailInvalid) return;
    await runSubmit(async () => {
      const result = await loginWithCredentials({ email, password, lang: chosenLang });
      if (result.status === 'MFA_REQUIRED') {
        onMfaRequired({ ...result, email });
      } else if (result.status === 'NEW_PASSWORD_REQUIRED') {
        onNewPasswordRequired({ session: result.session, email });
      } else if (result.status === 'MFA_SETUP_REQUIRED') {
        onMfaSetupRequired({ session: result.session, email });
      } else if (result.status === 'SELECT_MFA_TYPE') {
        onSelectMfaType({ ...result, email });
      } else {
        globalThis.location.href = '/';
      }
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          {t('login.local.description')}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          id="login-email"
          label={t('formField.Email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          autoFocus
          fullWidth
          size="small"
          error={isEmailInvalid}
          helperText={isEmailInvalid ? t('login.local.invalidEmail') : undefined}
          inputProps={{ maxLength: MAX_EMAIL, onAnimationStart: emailAutofill.onAnimationStart }}
          InputLabelProps={{ shrink: emailAutofill.shrink }}
          sx={autofillSx}
        />

        <TextField
          id="login-password"
          label={t('login.local.password')}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          fullWidth
          size="small"
          inputProps={{ maxLength: MAX_PASSWORD, onAnimationStart: passwordAutofill.onAnimationStart }}
          InputLabelProps={{ shrink: passwordAutofill.shrink }}
          sx={autofillSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t('login.local.togglePassword')}
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <SubmitButton loading={loading} disabled={!email || !password || isEmailInvalid}>
          {t('login.local.signIn')}
        </SubmitButton>

        <Button
          variant="text"
          size="small"
          onClick={onForgotPassword}
          sx={{ alignSelf: 'center', fontSize: '0.75rem' }}
        >
          {t('login.forgotPassword.link')}
        </Button>
      </Stack>
    </Box>
  );
};
